import { isNotNullish } from '@adornis/assert/isNullish.js';
import { UserPermission } from '@adornis/users/db/types.js';

export enum Role {
  /**
   * Internal roles for persons who actually work with the system. Describe the features these (probably only employees) can use
   */
  ADMIN = 'Admin',
  PSB = 'PSB',
  SCHULLEITER = 'Schulleiter',
  SEKRETAER = 'Sekretär',
  MAV = 'MAV',
  SUS_BEWERBER_BEARBEITUNG = 'SuS-Bewerber Bearbeitung',
  SPENDEN_ADMIN = 'SpendenAdmin',
  BUCHHALTUNG = 'Buchhaltung',
  IT = 'IT',
  /** receives the job ticket export by mail on the 10th of each month */
  JOB_TICKET_MANAGEMENT = 'Job-Ticket-Management',

  /**
   * Lifecycle roles of employees
   */
  KANDIDAT = 'Kandidat',
  MITARBEITER = 'Mitarbeiter',
  OLD_EMPLOYEE = 'Ehemaliger Mitarbeiter',

  /**
   * Lifecycle roles of students
   */
  SUS_BEWERBER = 'SuS-Bewerber',
  SCHUELER = 'Schüler',
  OLD_SCHUELER = 'Ehemaliger Schüler',
  OLD_SUS_BEWERBER = 'Ehemaliger SuS-Bewerber', // Not accepted -> was never active

  /**
   * Lifecycle roles of spender
   */
  SPENDER = 'Spender',
  OLD_SPENDER = 'Ehemaliger Spender',

  /**
   * Lifecycle roles of sbs
   */
  SUS_BEWERBER_SB = 'SuS-Bewerber Sorgeberechtigter',
  SORGEBERECHTIGTER = 'Sorgeberechtigter',
  OLD_SORGEBERECHTIGTER = 'Ehemaliger Sorgeberechtigter',
  OLD_SUS_BEWERBER_SB = 'Ehemaliger SuS-Bewerber Sorgeberechtigter', // Not accepted -> was never active

  // Temporary testing roles
  BETA_TESTING_AI = 'Beta Teaching Assistant',
  WIKI = 'Wiki',

  /** @deprecated muss konsequent abgeschafft werden, so dass kein Nutzer effektive Berechtigungen sieht */
  TICKET = 'Ticket',
}

/**
 * A lifecycle: the stages a person moves through, in order. Per school - and per child for Sorgeberechtigte and Spender
 * - exactly one stage is current, and the role recalculation moves the person on. All stages count as the same
 * "role" in the system: a Kandidat and an Ehemaliger Mitarbeiter are both employees.
 */
export interface RoleLifecycle {
  label: string;
  stages: Role[];
  /** the stage that counts as being one of them right now - Mitarbeiter, not Kandidat or Ehemaliger */
  active: Role;
  /**
   * The stage that is set by hand; the recalculation moves the person on from there. It stays assignable when the
   * person is already further along, and the next recalculation removes it again - by design, not locked anywhere.
   */
  start?: Role;
  /** branches off the first stage: an applicant who was not accepted, and so never became active */
  rejected?: Role;
  /** for the lifecycles that follow from other data only - a stage set by hand is gone with the next recalculation */
  derivedFrom?: string;
}

export const EMPLOYEE_LIFECYCLE: RoleLifecycle = {
  label: 'Mitarbeitende',
  stages: [Role.KANDIDAT, Role.MITARBEITER, Role.OLD_EMPLOYEE],
  active: Role.MITARBEITER,
  start: Role.KANDIDAT,
};

export const STUDENT_LIFECYCLE: RoleLifecycle = {
  label: 'Schüler',
  stages: [Role.SUS_BEWERBER, Role.SCHUELER, Role.OLD_SCHUELER],
  active: Role.SCHUELER,
  start: Role.SUS_BEWERBER,
  rejected: Role.OLD_SUS_BEWERBER,
};

export const SBS_LIFECYCLE: RoleLifecycle = {
  label: 'Sorgeberechtigte',
  stages: [Role.SUS_BEWERBER_SB, Role.SORGEBERECHTIGTER, Role.OLD_SORGEBERECHTIGTER],
  active: Role.SORGEBERECHTIGTER,
  rejected: Role.OLD_SUS_BEWERBER_SB,
  derivedFrom: 'Folgt aus den Kindern.',
};

export const SPENDER_LIFECYCLE: RoleLifecycle = {
  label: 'Spender',
  stages: [Role.SPENDER, Role.OLD_SPENDER],
  active: Role.SPENDER,
  derivedFrom: 'Folgt aus Spenden-Kindern und Spendenbuchungen.',
};

export const ROLE_LIFECYCLES = [EMPLOYEE_LIFECYCLE, STUDENT_LIFECYCLE, SBS_LIFECYCLE, SPENDER_LIFECYCLE];

/** Every role of a lifecycle, the rejection included. */
export const lifecycleRoles = ({ stages, rejected }: RoleLifecycle): Role[] =>
  rejected ? [...stages, rejected] : stages;

export const EMPLOYEE_ROLES = lifecycleRoles(EMPLOYEE_LIFECYCLE);
export const STUDENT_ROLES = lifecycleRoles(STUDENT_LIFECYCLE);
export const SPENDER_ROLES = lifecycleRoles(SPENDER_LIFECYCLE);
export const SBS_ROLES = lifecycleRoles(SBS_LIFECYCLE);

/**
 * The lifecycles as filter options, with "Alle" in front: which roles each one stands for, and which of them count
 * as active.
 */
export const SEMANTIC_ROLES = [
  { label: 'Alle', value: null, activeRole: null, inactiveRoles: null },
  ...[STUDENT_LIFECYCLE, SBS_LIFECYCLE, EMPLOYEE_LIFECYCLE, SPENDER_LIFECYCLE].map(lifecycle => ({
    label: lifecycle.label,
    value: lifecycleRoles(lifecycle),
    activeRole: [lifecycle.active],
    inactiveRoles: lifecycleRoles(lifecycle).filter(role => role !== lifecycle.active),
  })),
];

/**
 *  Roles which are determined by the system based on different environment parameters per person.
 *  e.g. Person X has an active contract on school Y so X gets the role "employee" on school Y
 *  These roles are here so an admin can see if the role is active or not. It cannot be altered via UI.
 *  generally these roles are all lifecycle roles which get assigned by the system
 */
export const DYNAMIC_ROLES = [...STUDENT_ROLES, ...EMPLOYEE_ROLES, ...SPENDER_ROLES, ...SBS_ROLES];

/**
 * Semantically active roles
 */
export const ACTIVE_ROLES = SEMANTIC_ROLES.flatMap(semanticRole => semanticRole.activeRole).filter(isNotNullish);

/**
 * Who works at Martinus: the system roles (the first block of `Role`) and the active employees. Everybody else -
 * students, Sorgeberechtigte, Spender, applicants and former employees - is outside.
 */
export const INTERNAL_ROLES = [
  Role.ADMIN,
  Role.PSB,
  Role.SCHULLEITER,
  Role.SEKRETAER,
  Role.MAV,
  Role.SUS_BEWERBER_BEARBEITUNG,
  Role.SPENDEN_ADMIN,
  Role.BUCHHALTUNG,
  Role.IT,
  Role.JOB_TICKET_MANAGEMENT,
  Role.MITARBEITER,
];

/**
 * Roles which can have a only a global context.
 * The PSB is not in this list as it is a special case and can be both locally and globaly.
 */
export const ONLY_GLOBAL_ROLES = [Role.ADMIN, Role.BETA_TESTING_AI, Role.JOB_TICKET_MANAGEMENT];

/**
 * Roles which can have a local and a global context.
 */
export const LOCAL_AND_GLOBAL_ROLES = [Role.PSB];

/**
 * Roles which should be displayed in the global role section.
 * This is a combination of roles which can have a local and a global context and roles which can only have a global context.
 */
export const GLOBAL_ROLES = [...LOCAL_AND_GLOBAL_ROLES, ...ONLY_GLOBAL_ROLES];

/**
 * @deprecated
 */
export enum Permission {
  AN_SEKTION_R = 'AN_SEKTION_R',
  AN_SEKTION_R_MAV = 'AN_SEKTION_R_MAV',
  AN_SEKTION_W = 'AN_SEKTION_W',
  AG_SEKTION_R = 'AG_SEKTION_R',
  /** Allows you to write Vertragsdaten for **other** people. */
  AG_SEKTION_W = 'AG_SEKTION_W',
  /** Allows you to write Vertragsdaten for youself. */
  AG_SEKTION_W_SELF = 'AG_SEKTION_W_SELF',
  PROFIL_SEKTION_R = 'PROFIL_SEKTION_R',
  PROFIL_SEKTION_R_MAV = 'PROFIL_SEKTION_R_MAV',
  PROFIL_SEKTION_W = 'PROFIL_SEKTION_W',
  SCHUELER_R = 'SCHUELER_R',
  SCHUELER_BEWERBER_R = 'SCHUELER_BEWERBER_R',
  SCHUELER_W = 'SCHUELER_W',
  VERLAUF = 'VERLAUF',
  RECHTEVERWALTUNG = 'RECHTEVERWALTUNG',
  DOWNLOAD = 'DOWNLOAD',
  ADP_EXPORT = 'ADP_EXPORT',
  NEUEINSTELLUNG_GENEHMIGEN_PSB = 'NEUEINSTELLUNG_GENEHMIGEN_PSB',
  NEUEINSTELLUNG_GENEHMIGEN_MAV = 'NEUEINSTELLUNG_GENEHMIGEN_MAV',
  ABWESENHEIT_GENEHMIGEN = 'ABWESENHEIT_GENEHMIGEN',
  DEBITORENNUMMER_W = 'DEBITORENNUMMER_W',
  LOESCHEN_MITARBEITER = 'LOESCHEN_MITARBEITER',
  LOESCHEN_SCHUELER = 'LOESCHEN_SCHUELER',
  LOESCHEN_PRODUKTE = 'LOESCHEN_PRODUKTE',
  RESEND_INVITE = 'RESEND_INVITE',
  CHANGE_EMAIL = 'CHANGE_EMAIL',
  CHANGE_USERNAME = 'CHANGE_USERNAME',
  CHANGE_NAME_ABBREVIATION = 'CHANGE_NAME_ABBREVIATION',
  TICKET_INVITE = 'TICKET_INVITE',
  EMAIL_SENDEN = 'EMAIL_SENDEN',
  CREATE_MICROSOFT_ACCOUNT = 'CREATE_MICROSOFT_ACCOUNT',
  EDIT_PERSON_MICROSOFT_ACCOUNT = 'EDIT_PERSON_MICROSOFT_ACCOUNT',
  VIEW_GROUP = 'VIEW_GROUP',
  EDIT_GROUP = 'EDIT_GROUP',
  CREATE_GROUP = 'CREATE_GROUP',
  DELETE_GROUP = 'DELETE_GROUP',
  WRITE_KOSTENSTELLE = 'WRITE_KOSTENSTELLE',
  UPLOAD_QUITTUNG = 'UPLOAD_QUITTUNG',
  INVITE_SORGEBERECHTIGTE = 'INVITE_SORGEBERECHTIGTE',
  INVITE_SPENDER = 'INVITE_SPENDER',
  MANUAL_SORGEBERECHTIGTE_CONFIRM = 'MANUAL_SORGEBERECHTIGTE_CONFIRM',
  MANUAL_SPENDER_CONFIRM = 'MANUAL_SPENDER_CONFIRM',
  REMOVE_SORGEBERECHTIGTE = 'REMOVE_SORGEBERECHTIGTE',
  REMOVE_SPENDER = 'REMOVE_SPENDER',
  TOGGLE_SPENDER_INVITE_TYPE = 'TOGGLE_SPENDER_INVITE_TYPE',
  DELETE_FILE = 'DELETE_FILES',
  SPENDEN_EXPORT = 'SPENDEN_EXPORT',
  ABWESENHEIT_EXPORT = 'ABWESENHEIT_EXPORT',
  TESTDATEN_ZUREUCKSETZEN = 'TESTDATEN_ZUREUCKSETZEN',
  ALLE_DOKUMENTE_SEHEN = 'ALLE_DOKUMENTE_SEHEN',
  LODAS_EXPORT = 'LODAS_EXPORT',
  CHANGE_EMAIL_ALIAS = 'CHANGE_EMAIL_ALIAS',
  READ_PLATFORM_USAGE = 'READ_PLATFORM_USAGE',
  IMPORT_PERSON = 'IMPORT_PERSON',
  MASS_USER_DELETION = 'MASS_USER_DELETION',

  VIEW_ADMIN_DASHBOARD = 'VIEW_ADMIN_DASHBOARD',

  // for viewing the basic list
  VIEW_PERSONS = 'VIEW_PERSONS',

  BOOKING_MASS_END = 'BOOKING_MASS_END',

  // for viewing the full list with every field and advanced filter
  VIEW_FULL_PERSONS = 'VIEW_FULL_PERSONS',

  EXPORT_TICKET = 'EXPORT_TICKET',

  CONTRACT_DELETE = 'CONTRACT_DELETE',

  CREATE_USER = 'CREATE_USER',
  VIEW_SUS_INTERVIEW_DATA = 'VIEW_SUS_INTERVIEW_DATA',

  VIEW_MAV_DOCUMENTS = 'VIEW_MAV_DOCUMENTS',
  VIEW_PSB_DOCUMENTS = 'VIEW_PSB_DOCUMENTS',

  VIEW_TEACHING_ASSISTANT = 'VIEW_TEACHING_ASSISTANT',

  ADMINISTRATIVE_SUS_INTERVIEWS = 'ADMINISTRATIVE_SUS_INTERVIEWS',

  EDIT_BOOKINGS = 'EDIT_BOOKINGS',
  ADD_BOOKINGS = 'ADD_BOOKINGS',
  DELETE_BOOKINGS = 'DELETE_BOOKINGS',
  /** Typ and Gruppen of an existing booking - set on creation, later changes are corrections for admins */
  EDIT_BOOKING_TYPE_AND_GROUPS = 'EDIT_BOOKING_TYPE_AND_GROUPS',

  VIEW_OTHER_SEPA_MANDATS = 'VIEW_OTHER_SEPA_MANDATS',
  EDIT_AP_TEMPLATES = 'EDIT_AP_TEMPLATES',

  ACCEPT_ALL_SEPA_MANDATES_FOR_BILLING_POSITION = 'ACCEPT_ALL_SEPA_MANDATES_FOR_BILLING_POSITION',

  VIEW_WIKI = 'VIEW_WIKI',
  EDIT_WIKI = 'EDIT_WIKI',

  // default permissions of users
  CHANGE_PASSWORD = UserPermission.ChangePassword,
  CHANGE_ROLES = UserPermission.ChangeRoles,
  IMPERSONATE = UserPermission.Impersonate,
}
